import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { formatarCEP } from "../../../../../../../helpers/masks";
import SearchIcon from '@mui/icons-material/Search';
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../lib/axios";
import { desformatarCEP } from "../../../../../../../helpers/unmask";
import { ViaCEPResposta } from "../../../../../../../types/externas/viacep-resultado";
import { useMemo } from "react";
import PaperDraggable from "../../../../../../../components/PaperDraggable";
import { enqueueSnackbar } from "notistack";
import { ICooperadoFazendaDetalhe } from "../../../../../../../types/api/cadastros/detalhar-cooperado";
import moment, { Moment } from "moment";
import { DatePicker } from "@mui/x-date-pickers";

export default function DialogEditarFazenda({
    open,
    onClose,
    data
}: DialogEditarFazendaProps) {
    const queryClient = useQueryClient();

    const ufs = useMemo(() => ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"], [])

    const methods = useForm<FormData>({
        defaultValues: {
            nome: data.nome,
            cep: desformatarCEP(data.cep) || "",
            inscricao: data.inscricao || "",
            inscricao_vencimento: (data.inscricao_vencimento && moment(data.inscricao_vencimento, "YYYY-MM-DD")) || null,
            apelido: `${data.apelido}` || "",
            cidade: data.cidade || "",
            uf: data.estado || "",
            regiao: data.regiao || "",
        }
    })
    const { control, watch, getValues, setValue, handleSubmit } = methods;

    //queries e mutations
    const { data: cidades } = useQuery({
        queryKey: ["pesquisar-cidades", watch(`uf`)],
        queryFn: async () => {
            if (watch(`uf`) === undefined) return []
            let result = await axiosClient.get("/pesquisar-cidades", {
                params: {
                    uf: getValues(`uf`)
                }
            })
            return result?.data
        }
    });
    const { dataCidades }: ICidades = {
        dataCidades: cidades || []
    }

    const { mutate: editar, isLoading } = useMutation({
        mutationKey: 'editar-cooperado',
        mutationFn: (inputs: EditarCooperado) => axiosClient.put(`/cadastros/editar-cooperado/${data.tipo_publico}${data.codigo_publico}`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao editar cooperado!', { variant: 'error' });
        },
        onSuccess: () => {
            enqueueSnackbar(`Cooperado editado com sucesso`, { variant: "success" });
            queryClient.invalidateQueries('cadastros/detalhar-cooperado')
            onClose()
        }
    })

    //

    //funções manipuladoras
    const handlePesquisarCep = async () => {
        const { cep } = getValues()
        if (cep === undefined || cep === "") return

        const unmaskCep = desformatarCEP(cep)

        try {
            const response = await axiosClient.get<ViaCEPResposta>(`https://viacep.com.br/ws/${unmaskCep}/json/`);
            const { localidade, uf } = response.data
            setValue(`uf`, uf)
            setValue(`cidade`, localidade)
        } catch (error) {
            console.error('Erro ao buscar endereço por CEP:', error)
            return
        }
    }

    const handleValidarDados = async (data: FormData) => {
        try {
            const response = await axiosClient.post(`/cadastros/verificar-inscricao-estadual`, {
                inscricao_estadual: getValues(`inscricao`),
                uf: getValues(`uf`),
            });
            if (response.status === 200) {
                onSubmit(data as FormData)
            }
        } catch (error) {
            enqueueSnackbar('Insira uma inscrição estadual válida!', { variant: 'error' })
            console.error('Erro ao validar Sintegra:', error)
            return
        }

    }

    const onSubmit = (data: FormData) => {
        const params: EditarCooperado = {
            campos: "Fazenda",
            nome: data.nome,
            inscricao: data.inscricao,
            inscricao_vencimento: data.inscricao_vencimento?.format("YYYY-MM-DD") || null,
            apelido: data.apelido,
            cep: desformatarCEP(data.cep),
            cidade: data.cidade,
            uf: data.uf,
            regiao: data.regiao || null
        }

        editar(params);
    }

    return (
        <FormProvider {...methods}>
            <Dialog
                maxWidth="md"
                fullWidth
                open={open}
                onClose={onClose}
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Editar Dados Cadastrais</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} mt={1}>
                        <Grid size={{ xs: 6 }}>
                            <Controller
                                name="nome"
                                control={control}
                                rules={{ required: "Campo obrigatório" }}
                                render={({ field, fieldState: { error, invalid } }) => {
                                    return (
                                        <TextField
                                            {...field}
                                            required
                                            error={invalid}
                                            label="Nome"
                                            fullWidth
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            slotProps={{ htmlInput: { maxLength: 40 } }}
                                            helperText={error ? error.message : ""}
                                        />
                                    );
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, md: 6 }}>
                            <Controller
                                name={`cep`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        label="CEP"
                                        variant="outlined"
                                        defaultValue={""}
                                        fullWidth
                                        required
                                        onChange={(e) => field.onChange(formatarCEP(e.target.value))}
                                        onBlur={handlePesquisarCep}
                                        error={invalid}
                                        helperText={error?.message || ""}
                                        slotProps={{
                                            input: {
                                                endAdornment: <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handlePesquisarCep}
                                                        edge="end"
                                                    >
                                                        <SearchIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                                name={`uf`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <FormControl fullWidth {...field} error={invalid} required>
                                        <InputLabel id="uf-select">UF</InputLabel>
                                        <Select
                                            labelId="uf-select"
                                            value={field.value}
                                            label="UF"
                                            onChange={(e) => field.onChange(e.target.value)}
                                        >
                                            {ufs.map((uf, index) => (
                                                <MenuItem key={index} value={uf}>{uf}</MenuItem>
                                            ))}
                                        </Select>
                                        {error && (
                                            <FormHelperText>{error?.message}</FormHelperText>
                                        )}
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, md: 5 }}>
                            <Controller
                                name={`cidade`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <>
                                        <Autocomplete
                                            {...field}
                                            fullWidth
                                            disablePortal
                                            options={dataCidades}
                                            defaultValue={""}
                                            onChange={(_, v) => field.onChange(v)}
                                            renderInput={(params) =>
                                                <TextField
                                                    {...params}
                                                    label="Cidade"
                                                    required
                                                    defaultValue={""}
                                                    error={Boolean(invalid)}
                                                    helperText={error?.message || ""}
                                                />
                                            }
                                        />
                                    </>
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, md: 4 }}>
                            <Controller
                                name={`regiao`}
                                control={control}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        label="Região"
                                        defaultValue={""}
                                        variant="outlined"
                                        fullWidth
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 6 }}>
                            <Controller
                                name={`inscricao`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        required
                                        label='Inscrição Fazenda'
                                        type="number"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) => field.onChange((e.target.value))}
                                        error={invalid}
                                        helperText={error ? error.message : ""}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 6 }}>
                            <Controller
                                name={`inscricao_vencimento`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <DatePicker
                                        {...field}
                                        sx={{ width: '100%' }}
                                        label="Vencimento inscrição"
                                        value={field.value ? moment(field.value) : null}
                                        minDate={moment()}
                                        onChange={(data) => field.onChange(data as Moment)}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                required: true,
                                                error: invalid,
                                                helperText: error ? error.message : "",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                    </Grid>

                </DialogContent>
                <DialogActions>
                    <Button disabled={isLoading} onClick={onClose}>Cancelar</Button>
                    <Button disabled={isLoading} onClick={handleSubmit(handleValidarDados)} color="primary" variant="contained">Editar</Button>
                </DialogActions>
            </Dialog>

        </FormProvider>
    )
}

interface DialogEditarFazendaProps {
    open: boolean;
    onClose: () => void;
    data: ICooperadoFazendaDetalhe
}

type FormData = {
    nome: string;
    inscricao: string;
    inscricao_vencimento: Moment | null;
    apelido: string;
    cep: string;
    cidade: string;
    uf: string;
    regiao: string;
};

type EditarCooperado = {
    campos: "Fazenda";
    nome: string;
    inscricao: string;
    inscricao_vencimento: string | null;
    apelido: string;
    cep: string;
    cidade: string;
    uf: string;
    regiao: string | null;
}

interface ICidades {
    dataCidades: Array<string>
}